import { Component, effect, inject, signal, computed } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, finalize, of, switchMap } from 'rxjs';
import { DynamicFilterConfig, ModalReturnData, createDynamicFilterState, ModalService } from '@card-software/library';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from 'primeng/api';
import { PaginatorState } from 'primeng/paginator';
import {
  SELF_ISSUED_ENTITY_DEFINITIONS,
  TaxAuthorization,
  authorizationCoverageLabel,
  authorizationEntityLabel,
} from '../../models/tax-authorization/tax-authorization';
import { Business } from '../../models/business';
import { TaxAuthorizationProxyService } from '../../proxies/tax-authorization.proxy.service';
import { SiteBusinessService } from '../../services/site-business.service';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { TaxAuthorizationDetailsModalComponent } from './tax-authorization-details-modal/tax-authorization-details-modal.component';

interface TaxAuthorizationFilters {
  entityDefinitionCode?: number | string | null;
  authorizationNumber?: string | null;
  active?: boolean | string | null;
}

/**
 * Tax Authorizations dashboard — the fiscal ranges THIS SITE issues under its own name (#4791).
 *
 * <p>Scoped to the site's own company, which is why there is no business filter: every row here belongs to
 * the same holder. What varies is the document a range numbers — AP documents, Dispatch Instructions,
 * Remission Guides, Second Quality Dispatches — and this is the only screen where any of them can be
 * registered.</p>
 *
 * <p>A counterparty's own authorization is the other half of the picture and lives on the Tax
 * Authorizations tab of Business Details, next to the provider it belongs to.</p>
 *
 * <p>System-managed rows (<c>isSystem</c>) are read-only: the dashboard hides their edit/deactivate
 * actions.</p>
 */
@Component({
  selector: 'app-tax-authorizations',
  templateUrl: './tax-authorizations.component.html',
  styleUrl: './tax-authorizations.component.scss',
})
export class TaxAuthorizationsComponent {
  private readonly proxy = inject(TaxAuthorizationProxyService);
  private readonly siteBusinessService = inject(SiteBusinessService);
  private readonly modalService = inject(ModalService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly translateService = inject(TranslateService);

  private lastCriteria: { entityDefinitionCode?: number; authorizationNumber?: string; active?: boolean } = {};

  isLoading = signal(0);
  filterConfig = signal<DynamicFilterConfig | null>(null);
  showFilters = signal(true);
  readonly filterState = createDynamicFilterState({ storageKey: 'tax-authorization-filters', config: this.filterConfig });
  records = signal<TaxAuthorization[]>([]);

  /** The site's own company — the holder of every range on this page. Null until resolved, and stays null
   *  when the site has no internal-company record, which the empty state explains. */
  siteBusiness = signal<Business | null>(null);
  /** Distinguishes "still resolving" from "resolved to nothing", so the empty state does not flash. */
  siteBusinessResolved = signal(false);

  readonly totalRecords = computed(() => this.records().length);
  readonly currentPage = signal(1);
  readonly pageSize = signal(25);

  readonly paginatedRecords = computed(() => {
    const start = (this.currentPage() - 1) * this.pageSize();
    return this.records().slice(start, start + this.pageSize());
  });

  readonly canCreate = computed(() => !!this.siteBusiness()?.id);

  /** The server's account of why the site has no own business, when it gave one — it names the businesses
   *  involved, which a generic "not configured" cannot. */
  readonly siteBusinessError = this.siteBusinessService.resolutionError;

  constructor() {
    this.filterState.applied$.pipe(takeUntilDestroyed()).subscribe((filters) => this.searchWithFilters(filters as TaxAuthorizationFilters));

    effect(
      () => {
        if (this.translateService.currentLang) {
          this.tryInitializeFilterConfig();
        }
      },
      { allowSignalWrites: true },
    );
  }

  private startLoading(): void {
    this.isLoading.update((v) => v + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((v) => v - 1);
  }

  private translate(key: string, fallback: string): string {
    const translation = this.translateService.instant(key);
    return translation === key ? fallback : translation;
  }

  private tryInitializeFilterConfig(): void {
    this.filterConfig.set(this.buildFilterConfig());
  }

  private buildFilterConfig(): DynamicFilterConfig {
    return {
      persistInUrl: true,
      persistVisibility: true,
      filters: [
        {
          type: 'select',
          formControlName: 'entityDefinitionCode',
          label: this.translate('TaxAuthorization_Entity', 'Document'),
          placeholder: this.translate('TaxAuthorization_Entity_Placeholder', 'All documents'),
          options: SELF_ISSUED_ENTITY_DEFINITIONS.map((entity) => ({
            label: this.translate(`TaxAuthorization_Entity_${entity.value}`, entity.label),
            value: entity.value.toString(),
          })),
          showClear: true,
          defaultVisible: true,
        },
        {
          type: 'text',
          formControlName: 'authorizationNumber',
          label: this.translate('TaxAuthorization_Number', 'Authorization Number'),
          placeholder: this.translate('Cai_Number_Placeholder', 'Enter CAI number'),
          defaultVisible: true,
        },
        {
          type: 'select',
          formControlName: 'active',
          label: this.translate('Status', 'Status'),
          placeholder: this.translate('Select_status', 'Select status'),
          options: [
            { label: this.translate('Active', 'Active'), value: 'true' },
            { label: this.translate('Inactive', 'Inactive'), value: 'false' },
          ],
          showClear: true,
          defaultVisible: true,
        },
      ],
    };
  }

  onPageChange(event: PaginatorState): void {
    this.currentPage.set((event.page ?? 0) + 1);
    this.pageSize.set(event.rows ?? this.pageSize());
  }

  // ── Search ──────────────────────────────────────────────────────────────────
  private searchWithFilters(filters: TaxAuthorizationFilters): void {
    const entityDefinitionCode = filters.entityDefinitionCode ? Number(filters.entityDefinitionCode) : undefined;
    const authorizationNumber = (filters.authorizationNumber ?? '').toString().trim() || undefined;
    const active =
      filters.active === null || filters.active === undefined || filters.active === ''
        ? undefined
        : filters.active === true || filters.active === 'true';

    this.lastCriteria = {
      entityDefinitionCode: Number.isFinite(entityDefinitionCode) ? entityDefinitionCode : undefined,
      authorizationNumber,
      active,
    };
    this.search();
  }

  private search(): void {
    const { entityDefinitionCode, authorizationNumber, active } = this.lastCriteria;
    this.startLoading();

    // The holder is the scope, so it has to resolve before the search can run at all — chained rather than
    // fetched in parallel. It is cached after the first call, so this costs one request per session.
    this.siteBusinessService
      .getSiteBusiness()
      .pipe(
        switchMap((business) => {
          this.siteBusiness.set(business);
          this.siteBusinessResolved.set(true);
          if (!business?.id) {
            return of({ success: true, data: [] as TaxAuthorization[], errorMessage: null });
          }
          return this.proxy.search(business.id, active, entityDefinitionCode, authorizationNumber);
        }),
        catchError(() => of({ success: false, data: [] as TaxAuthorization[], errorMessage: null })),
        finalize(() => this.stopLoading()),
      )
      .subscribe((response) => {
        this.records.set(response.success && response.data ? response.data : []);
        this.currentPage.set(1);
      });
  }

  /** Display label for the document a range numbers. */
  entityLabel(entityDefinitionCode: number): string {
    return authorizationEntityLabel(entityDefinitionCode);
  }

  /** What an AP range covers; empty for the dispatch documents, which carry it in their entity definition. */
  coverageLabel(documentTypes: number[] | null | undefined): string {
    return authorizationCoverageLabel(documentTypes);
  }

  // ── Create / edit (drawer via ModalService) ──────────────────────────────────
  openCreate(): void {
    this.openModal(null);
  }

  openEdit(record: TaxAuthorization): void {
    if (record.isSystem) {
      return;
    }
    this.openModal(record);
  }

  private openModal(authorization: TaxAuthorization | null): void {
    const business = this.siteBusiness();
    if (!business?.id) {
      return;
    }

    this.modalService
      .open(
        TaxAuthorizationDetailsModalComponent,
        {},
        { authorization, mode: 'self-issued', businessId: business.id, businessName: business.name },
      )
      .subscribe((result: ModalReturnData<TaxAuthorization | null>) => {
        if (result.action === 'Save') {
          this.search();
        }
      });
  }

  // ── Deactivate ──────────────────────────────────────────────────────────────
  confirmDeactivate(record: TaxAuthorization): void {
    this.confirmationService.confirm({
      header: this.translate('Cai_Deactivate_Title', 'Deactivate authorization'),
      message: this.translate('Cai_Deactivate_Message', 'Deactivate authorization {cai}? It will no longer issue numbers.').replace(
        '{cai}',
        record.authorizationNumber,
      ),
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => this.deactivate(record),
    });
  }

  private deactivate(record: TaxAuthorization): void {
    this.proxy
      .deactivate(record.id)
      .pipe(catchError(() => of(null)))
      .subscribe((response) => {
        if (response === null || !response.success) {
          return;
        }
        GlobalMessageUtil.success('Tax Authorizations', 'Tax authorization deactivated.');
        this.search();
      });
  }

  trackByRecordId = (_: number, record: TaxAuthorization) => record.id;

  getRowData(rowData: unknown): TaxAuthorization {
    return rowData as TaxAuthorization;
  }
}
